import React from "react";
import { divisionServices, type Division } from "@src/api/dictionaries/divisions";
import { divisionSectionApi } from "@src/api/dictionaries/sections";
import { formatDivisionText } from "@src/components/business-data-display";
import { PickerOptionCard } from "@shared/ui/picker-option";
import { BusinessSearchSelect, type BusinessSearchSelectProps } from "../BusinessSearchSelect";

export type DivisionSectionSearchSelectProps = Omit<
    BusinessSearchSelectProps<Division, number>,
    "queryKeyBase" | "loadOptions" | "loadById" | "getId" | "getOptionLabel" | "getSelectedLabel"
> & {
    /**
     * Код подразделения-контекста для `avSections(CurrentDivCode)`. \
     * Без валидного кода Select недоступен (зависимый режим); свободный выбор участка — follow-up.
     */
    divisionCode?: number | null;
};

function resolveDivisionCardTitle(item: Division): string {
    const divTypeName = item.DivisionType?.name ?? "<неизвестный тип>";
    const primaryText = formatDivisionText(item, null);

    if (primaryText != null && primaryText !== "") {
        return `<${divTypeName}> ${primaryText}`;
    }
    return divTypeName;
}

/**
 * SearchSelect одиночного выбора участка в контексте подразделения. \
 * Options: `avSections` → hydrate через `storeDivisions` (`SectionCodes` + `DivisionType`); \
 * value / `getId` — {@link Division.SectionCode}; отображение как у {@link DivisionSearchSelect}.
 */
export function DivisionSectionSearchSelect({
    label = "Участок",
    divisionCode,
    minSearchLength = 2,
    debounceDelay = 500,
    searchPlaceholder = "Введите обозначение участка",
    value,
    onChange,
    optionSlots,
    disabled: disabledProp,
    ...rest
}: DivisionSectionSearchSelectProps) {
    const canLoad = isValidCode(divisionCode);
    const disabled = disabledProp === true || !canLoad;

    return (
        <BusinessSearchSelect<Division, number>
            queryKeyBase={["searchSelect", "divisionSection", divisionCode]}
            loadOptions={async (query, signal) => {
                if (!isValidCode(divisionCode)) {
                    return [];
                }
                return resolveDivisionSectionOptions(divisionCode, query, signal);
            }}
            loadById={async (code, signal) => resolveDivisionSectionById(code, signal)}
            getId={(item) => item.SectionCode}
            getOptionLabel={(item) => formatDivisionText(item, null)}
            getSelectedLabel={(item) => formatDivisionText(item, null)}
            value={value}
            onChange={onChange}
            label={label}
            searchPlaceholder={searchPlaceholder}
            minSearchLength={minSearchLength}
            debounceDelay={debounceDelay}
            disabled={disabled}
            {...rest}
            optionSlots={{
                ...optionSlots,
                fullscreen:
                    optionSlots?.fullscreen ??
                    ((item) => {
                        const title = resolveDivisionCardTitle(item);
                        const name = item.Name?.trim();
                        const wsCode = item.WSCode?.trim();
                        return (
                            <PickerOptionCard
                                title={title}
                                detailsLayout="inline"
                                details={[
                                    {
                                        label: "Наименование",
                                        value: name != null && name !== "" && name !== title ? name : undefined,
                                    },
                                    {
                                        label: "Цех. код",
                                        value: wsCode != null && wsCode !== "" && wsCode !== title ? wsCode : undefined,
                                    },
                                ]}
                            />
                        );
                    }),
            }}
        />
    );
}

/**
 * Options: `avSections` → `loadBySectionCodes` (+ `DivisionType`), затем клиентский filter по строке поиска.
 * @param divisionCode код подразделения-контекста
 * @param query строка поиска; пустая — полный hydrated список
 * @param signal AbortSignal запроса
 */
async function resolveDivisionSectionOptions(
    divisionCode: number,
    query: string,
    signal?: AbortSignal,
): Promise<Division[]> {
    const available = await divisionSectionApi.loadAvailable({ CurrentDivCode: divisionCode }, { signal });
    const sectionCodes = available.map((item) => item.code).filter((code) => code !== 0);
    if (sectionCodes.length === 0) {
        return [];
    }

    const items = await divisionServices.loadBySectionCodes({ sectionCodes, include: ["DivisionType"] }, { signal });
    const bySectionCode = new Map(items.map((item) => [item.SectionCode, item]));
    const ordered = available.map((item) => bySectionCode.get(item.code)).filter((item): item is Division => item != null);

    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (normalizedQuery === "") {
        return ordered;
    }

    return ordered.filter((item) => matchesDivisionSearch(item, normalizedQuery));
}

/**
 * Resolve выбранного участка через `storeDivisions` + `SectionCodes`.
 * @param sectionCode код участка
 * @param signal AbortSignal запроса
 */
async function resolveDivisionSectionById(sectionCode: number, signal?: AbortSignal): Promise<Division | null> {
    if (!isValidCode(sectionCode)) {
        return null;
    }

    const items = await divisionServices.loadBySectionCodes(
        { sectionCodes: [sectionCode], include: ["DivisionType"] },
        { signal },
    );
    return items.find((item) => item.SectionCode === sectionCode) ?? items[0] ?? null;
}

function matchesDivisionSearch(item: Division, normalizedQuery: string): boolean {
    const label = formatDivisionText(item, null).toLocaleLowerCase();
    if (label.includes(normalizedQuery)) {
        return true;
    }

    const fields = [item.Sign, item.Name, item.WSCode, item.FullName ?? ""];
    return fields.some((field) => field.toLocaleLowerCase().includes(normalizedQuery));
}

function isValidCode(code: number | null | undefined): code is number {
    return code != null && code !== 0;
}
